// Single line diagram: lays the project's switchboards out as A3 sheets of simple shapes (mm),
// rendered both as SVG on screen and into the PDF (utils/sldRender.js).
//
// Sheet 1 is an overview (sources → boards → transformers → sub-boards); then one or more sheets per
// switchboard: busbar(s) with incomers above, outgoing breakers below and a circuit table under them.

export const PAGE = { w: 420, h: 297 };
const FRAME = { x: 10, y: 10, w: 400, h: 277 };
const AREA  = { x1: 18, y1: 18, x2: 402, y2: 242 };
const TB    = { x: 230, y: 247, w: 180, h: 40 };   // title block

const MM_PER_PT = 0.3528;
const SOURCE_KINDS_FED = ['transformer', 'ups', 'other'];

// ── Primitives ──
const line   = (x1, y1, x2, y2, w = 0.25, extra = {}) => ({ t: 'line', x1, y1, x2, y2, w, ...extra });
const rect   = (x, y, w, h, lw = 0.25) => ({ t: 'rect', x, y, w, h, lw });
const circle = (cx, cy, r, w = 0.3) => ({ t: 'circle', cx, cy, r, w });
const tri    = (pts) => ({ t: 'tri', pts });
const text   = (s, x, y, size = 6, opts = {}) => ({ t: 'text', s: String(s ?? ''), x, y, size, anchor: 'start', angle: 0, bold: false, ...opts });

// Rough Helvetica width, good enough to keep text inside its box
const textWidth = (s, size) => String(s).length * size * MM_PER_PT * 0.52;
const fit = (s, maxMm, size) => {
  s = String(s ?? '');
  if (textWidth(s, size) <= maxMm) return s;
  const n = Math.max(1, Math.floor(maxMm / (size * MM_PER_PT * 0.52)) - 1);
  return s.slice(0, n) + '…';
};
const capH = (size) => size * MM_PER_PT * 0.72;

const num = (v, d = 1) => (v == null || v === '' ? '' : Number(v).toLocaleString('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: d }));

// Rotated (bottom-to-top) lines centred on x, starting at yBottom and at most `len` mm long
function rotLines(out, lines, x, yBottom, len, size = 6, lead = 2.6, opts = {}) {
  const list = lines.filter(l => l && l.s !== '');
  const n = list.length;
  list.forEach((l, i) => {
    const bx = x - (n * lead) / 2 + lead * i + capH(size) + (lead - capH(size)) / 2;
    out.push(text(fit(l.s, len, l.size || size), bx, yBottom, l.size || size, { angle: 90, bold: !!l.bold, ...opts }));
  });
}

// ── Symbols (IEC 60617 style) ──
// Circuit breaker, vertical: fixed contact (×) at yTop, moving contact pivoting at yBot
function breakerV(out, x, yTop, yBot) {
  out.push(line(x, yBot, x - 3.2, yTop + 1.6, 0.3));
  out.push(line(x - 1.1, yTop - 1.1, x + 1.1, yTop + 1.1, 0.3));
  out.push(line(x - 1.1, yTop + 1.1, x + 1.1, yTop - 1.1, 0.3));
}
// Circuit breaker, horizontal (bus tie): pivot at xL, fixed contact (×) at xR
function breakerH(out, xL, xR, y) {
  out.push(line(xL, y, xR - 1.6, y - 3.2, 0.3));
  out.push(line(xR - 1.1, y - 1.1, xR + 1.1, y + 1.1, 0.3));
  out.push(line(xR - 1.1, y + 1.1, xR + 1.1, y - 1.1, 0.3));
}
const arrowDown = (out, x, y) => out.push(tri([[x - 1.1, y - 2.4], [x + 1.1, y - 2.4], [x, y]]));
const arrowUp   = (out, x, y) => out.push(tri([[x - 1.1, y + 2.4], [x + 1.1, y + 2.4], [x, y]]));

// Source symbol centred on (cx, cy); returns its half height
function sourceSymbol(out, kind, cx, cy) {
  if (kind === 'generator') {
    out.push(circle(cx, cy, 5.5));
    out.push(text('G', cx, cy + capH(9) / 2 - 0.6, 9, { anchor: 'middle', bold: true }));
    out.push(text('~', cx, cy + 3.6, 6, { anchor: 'middle' }));
    return 5.5;
  }
  if (kind === 'transformer') {
    out.push(circle(cx, cy - 2.8, 4.2));
    out.push(circle(cx, cy + 2.8, 4.2));
    return 7;
  }
  if (kind === 'battery') {
    out.push(line(cx - 5, cy - 1.2, cx + 5, cy - 1.2, 0.3));
    out.push(line(cx - 2.8, cy + 1.2, cx + 2.8, cy + 1.2, 0.9));
    out.push(line(cx, cy - 4, cx, cy - 1.2, 0.25));
    out.push(line(cx, cy + 1.2, cx, cy + 4, 0.25));
    out.push(text('+', cx + 3.2, cy - 2, 5));
    return 4;
  }
  const label = { shore: 'SHORE', ups: 'UPS' }[kind] || 'SUPPLY';
  out.push(rect(cx - 6, cy - 4, 12, 8, 0.3));
  out.push(text(label, cx, cy + capH(5) / 2, 5, { anchor: 'middle', bold: true }));
  return 4;
}

const ratingText = (s) => [
  s.kva != null && `${num(s.kva)} kVA`,
  s.kw != null && `${num(s.kw)} kW`,
  s.cosPhi != null && `cos phi ${num(s.cosPhi, 2)}`,
  s.voltage,
  s.current_a != null && `${num(s.current_a)} A`,
].filter(Boolean).join(' / ');

// ── Frame and title block ──
function frame(page, info) {
  const o = [];
  o.push(rect(FRAME.x, FRAME.y, FRAME.w, FRAME.h, 0.5));
  const { x, y, w } = TB;
  o.push(rect(x, y, w, TB.h, 0.4));
  // Rows: 15 (logo + title) | 10 (client / vessel) | 7.5 | 7.5
  o.push(line(x, y + 15, x + w, y + 15, 0.3));
  o.push(line(x, y + 25, x + w, y + 25, 0.3));
  o.push(line(x, y + 32.5, x + w, y + 32.5, 0.2));
  o.push(line(x + 60, y, x + 60, y + 15, 0.3));
  o.push({ t: 'logo', x: x + 6, y: y + 1.8, w: 40, h: 40 / (3181.136 / 927.334) });
  o.push(text('SINGLE LINE DIAGRAM', x + 63, y + 6.5, 10, { bold: true }));
  o.push(text(fit(page.title, w - 66, 8), x + 63, y + 12, 8));

  const cell = (x1, x2, yTop, h, label, value, size = 7) => {
    o.push(text(label, x1 + 1.5, yTop + 2.6, 4.5));
    o.push(text(fit(value, x2 - x1 - 3, size), x1 + 1.5, yTop + h - 1.4, size, { bold: true }));
  };
  o.push(line(x + 90, y + 15, x + 90, y + 25, 0.2));
  cell(x, x + 90, y + 15, 10, 'CLIENT', info.client);
  cell(x + 90, x + w, y + 15, 10, 'VESSEL', info.vessel);

  const r1 = [[0, 70, 'DRAWING NO.', info.drawingNo], [70, 90, 'REV.', info.revision], [90, 120, 'DATE', info.date],
    [120, 150, 'DRAWN', info.drawnBy], [150, 180, 'CHECKED', info.checkedBy]];
  for (const [a, b, label, value] of r1) {
    if (a) o.push(line(x + a, y + 25, x + a, y + 32.5, 0.2));
    cell(x + a, x + b, y + 25, 7.5, label, value, 6);
  }
  const r2 = [[0, 70, 'NEWBUILDING NO.', info.nb], [70, 150, 'PROJECT', info.project], [150, 180, 'SHEET', `${page.no} / ${page.total}`]];
  for (const [a, b, label, value] of r2) {
    if (a) o.push(line(x + a, y + 32.5, x + a, y + 40, 0.2));
    cell(x + a, x + b, y + 32.5, 7.5, label, value, 6);
  }
  return o;
}

// ── Switchboard sheets ──
const BUS_Y = 92;
const TABLE_Y = BUS_Y + 26;
const ROWS = [
  { key: 'breaker',  label: 'Breaker',        h: 5 },
  { key: 'type',     label: 'Type / setting', h: 22 },
  { key: 'cable',    label: 'Cable',          h: 26 },
  { key: 'consumer', label: 'Consumer',       h: 50 },
  { key: 'kw',       label: 'kW',             h: 5 },
];
const SUB_W = 12, IN_W = 16, X0 = 64, X_MAX = AREA.x2 - 4;

function boardColumns(board, data) {
  const own = new Set(board.breakers.map(b => b._id));
  const byId = new Map(board.breakers.map(b => [b._id, b]));
  const sectioned = board.sections.length > 0;
  const known = new Set(board.sections);
  const sectionOf = (brk) => (!sectioned ? '' : known.has(brk?.section) ? brk.section : board.sections[0]);

  const used = new Set();
  const cols = [];

  // Incomers: sources supplying this board, and the feed from another board
  for (const s of data.sources.filter(s => s.switchboard === board._id)) {
    const brk = own.has(s.incomingBreaker) ? byId.get(s.incomingBreaker) : null;
    if (brk) used.add(brk._id);
    cols.push({ kind: 'in', w: IN_W, section: sectionOf(brk), breaker: brk, source: s });
  }
  if (board.fedFrom) {
    const brk = own.has(board.incomingBreaker) ? byId.get(board.incomingBreaker) : null;
    if (brk) used.add(brk._id);
    cols.push({ kind: 'in', w: IN_W, section: sectionOf(brk), breaker: brk, feed: board.fedFrom });
  }

  // Bus ties: between adjacent sections of this board in the busbar, otherwise drawn upwards
  const gapTies = new Map();
  for (const b of board.breakers.filter(b => b.tieBoard)) {
    used.add(b._id);
    const a = board.sections.indexOf(sectionOf(b));
    const t = b.tieBoard === board._id ? board.sections.indexOf(b.tieSection) : -1;
    if (b.tieBoard === board._id && a >= 0 && t >= 0 && Math.abs(a - t) === 1 && !gapTies.has(Math.min(a, t))) {
      gapTies.set(Math.min(a, t), b);
    } else {
      const other = data.boards.find(x => x._id === b.tieBoard);
      cols.push({ kind: 'tie', w: IN_W, section: sectionOf(b), breaker: b, to: `${other?.name || ''}${b.tieSection ? ` ${b.tieSection}` : ''}`.trim() });
    }
  }

  for (const b of board.breakers) {
    if (used.has(b._id)) continue;
    const n = Math.max(1, b.circuits.length);
    cols.push({ kind: 'out', w: SUB_W * n, section: sectionOf(b), breaker: b });
  }

  // Order: per section, incomers → ties → outgoing; a gap (with its tie breaker, if any) between sections
  const order = { in: 0, tie: 1, out: 2 };
  const secs = sectioned ? board.sections : [''];
  const result = [];
  secs.forEach((sec, i) => {
    if (i > 0) result.push({ kind: 'gap', w: gapTies.has(i - 1) ? 16 : 8, tie: gapTies.get(i - 1) || null, between: [secs[i - 1], sec] });
    if (sectioned) result.push({ kind: 'label', w: 11, section: sec });
    result.push(...cols.filter(c => c.section === sec).sort((a, b) => order[a.kind] - order[b.kind]));
  });
  return result;
}

function feedText(f) {
  if (f.kind === 'board') return `TO ${f.name}${f.voltage ? ` (${f.voltage})` : ''}`;
  return `TO ${f.boardName} VIA ${f.name}`;
}

function drawOutgoing(o, col, x0, opts) {
  const b = col.breaker;
  const subs = b.circuits.length ? b.circuits : [null];
  const cx = x0 + col.w / 2;
  o.push(line(cx, BUS_Y, cx, BUS_Y + 6));
  breakerV(o, cx, BUS_Y + 6, BUS_Y + 16);
  const spare = !b.circuits.length && !b.feeds.length;
  if (subs.length > 1) {
    o.push(line(cx, BUS_Y + 16, cx, BUS_Y + 19));
    o.push(line(x0 + SUB_W / 2, BUS_Y + 19, x0 + col.w - SUB_W / 2, BUS_Y + 19));
  }
  subs.forEach((c, i) => {
    const sx = x0 + SUB_W * (i + 0.5);
    const yFrom = subs.length > 1 ? BUS_Y + 19 : BUS_Y + 16;
    if (spare) { o.push(line(sx, yFrom, sx, BUS_Y + 20)); return; }
    o.push(line(sx, yFrom, sx, TABLE_Y - 2.4));
    arrowDown(o, sx, TABLE_Y);
  });

  // Circuit table cells
  let y = TABLE_Y;
  const rowY = {};
  for (const r of ROWS) { rowY[r.key] = y; y += r.h; }
  o.push(text(fit(b.tag, col.w - 1, 5.5), cx, rowY.breaker + 3.7, 5.5, { anchor: 'middle', bold: true }));
  rotLines(o, [{ s: b.type }, { s: b.setting }], cx, rowY.type + ROWS[1].h - 1, ROWS[1].h - 2, 5.5);
  subs.forEach((c, i) => {
    const sx = x0 + SUB_W * (i + 0.5);
    if (opts.cables !== false) rotLines(o, [{ s: c?.cable?.tag || '' }, { s: c?.cable?.type || '' }], sx, rowY.cable + ROWS[2].h - 1, ROWS[2].h - 2, 5.5);
    const lines = [];
    if (i === 0 && b.feeds.length) lines.push({ s: b.feeds.map(feedText).join(', '), bold: true });
    if (c?.consumer) lines.push({ s: c.consumer.tag, bold: !b.feeds.length }, { s: c.consumer.description });
    if (spare) lines.push({ s: 'SPARE', bold: true });
    rotLines(o, lines.slice(0, 3), sx, rowY.consumer + ROWS[3].h - 1, ROWS[3].h - 2, 5.5);
    if (opts.kw !== false && c?.consumer?.kw != null) o.push(text(num(c.consumer.kw, 2), sx, rowY.kw + 3.7, 5, { anchor: 'middle' }));
  });
  // Cell borders: breaker rows span the whole breaker, the rest one per circuit
  o.push(line(x0 + col.w, TABLE_Y, x0 + col.w, y, 0.15));
  for (let i = 1; i < subs.length; i++) o.push(line(x0 + SUB_W * i, rowY.cable, x0 + SUB_W * i, y, 0.15));
}

function drawIncoming(o, col, x0) {
  const cx = x0 + col.w / 2;
  const b = col.breaker;
  let top = BUS_Y;
  if (b) {
    o.push(line(cx, BUS_Y, cx, BUS_Y - 6));
    breakerV(o, cx, BUS_Y - 16, BUS_Y - 6);
    rotLines(o, [{ s: [b.tag, b.type, b.setting].filter(Boolean).join('  ') }], cx + 5.2, BUS_Y - 1.5, 22, 5);
    top = BUS_Y - 16;
  }
  o.push(line(cx, top, cx, BUS_Y - 24));
  const lines = [];
  let symTop;
  if (col.kind === 'tie') {
    arrowUp(o, cx, BUS_Y - 26.4);
    symTop = BUS_Y - 27;
    lines.push({ s: 'BUS TIE', bold: true }, { s: `TO ${col.to}` });
  } else if (col.source) {
    const s = col.source;
    const h = sourceSymbol(o, s.kind, cx, BUS_Y - 24 - (s.kind === 'transformer' ? 7 : 5.5));
    symTop = BUS_Y - 24 - (s.kind === 'transformer' ? 7 : 5.5) - h;
    lines.push({ s: s.name, bold: true }, { s: ratingText(s) });
    if (s.fedFrom) lines.push({ s: `FROM ${s.fedFrom.tag} (${s.fedFrom.boardName})` });
  } else {
    arrowUp(o, cx, BUS_Y - 26.4);
    symTop = BUS_Y - 27;
    lines.push({ s: `FROM ${col.feed.tag}`, bold: true }, { s: col.feed.boardName });
  }
  rotLines(o, lines, cx, symTop - 2, symTop - 2 - (AREA.y1 + 2), 5.5);
}

function boardPages(board, data, opts) {
  const cols = boardColumns(board, data);
  // Split into sheets; a section gap never starts a sheet
  const sheets = [];
  let cur = [], x = X0;
  for (const c of cols) {
    if (c.kind === 'gap' && !cur.length) continue;
    if (cur.length && x + c.w > X_MAX) {
      sheets.push(cur); cur = []; x = X0;
      if (c.kind === 'gap') continue;
      // A section carried over to the next sheet is labelled again
      if (board.sections.length && c.kind !== 'label') { cur.push({ kind: 'label', w: 11, section: c.section, x }); x += 11; }
    }
    cur.push({ ...c, x }); x += c.w;
  }
  if (cur.length || !sheets.length) sheets.push(cur);

  return sheets.map((sheetCols, si) => {
    const o = [];
    // Board name on the left
    o.push(text(board.name, AREA.x1 + 2, BUS_Y - 4, 14, { bold: true }));
    if (board.voltage) o.push(text(board.voltage, AREA.x1 + 2, BUS_Y + 2, 8));
    if (board.description) {
      const words = board.description.split(/\s+/); const lines = [''];
      for (const w of words) { const t = `${lines[lines.length - 1]} ${w}`.trim(); if (textWidth(t, 6) > X0 - AREA.x1 - 6 && lines[lines.length - 1]) lines.push(w); else lines[lines.length - 1] = t; }
      lines.slice(0, 3).forEach((l, i) => o.push(text(l, AREA.x1 + 2, BUS_Y + 7 + i * 2.8, 6)));
    }

    // Busbar segments per section run
    const runs = [];
    for (const c of sheetCols) {
      if (c.kind === 'gap') { runs.push(null); continue; }
      const last = runs[runs.length - 1];
      if (last && last.section === c.section) last.x2 = c.x + c.w;
      else runs.push({ section: c.section, x1: c.x, x2: c.x + c.w });
    }
    const segs = runs.filter(Boolean);
    for (const r of segs) {
      o.push(line(r.x1 + 1, BUS_Y, r.x2 - 1, BUS_Y, 1.4));

    }
    if (!segs.length) o.push(line(X0, BUS_Y, X0 + 40, BUS_Y, 1.4));
    if (si > 0) o.push(text(`CONT. FROM SHEET ${'{prev}'}`, X0, BUS_Y - 2.5, 5));
    if (si < sheets.length - 1) {
      const xe = (sheetCols[sheetCols.length - 1]?.x || X0) + (sheetCols[sheetCols.length - 1]?.w || 0);
      o.push(text(`CONT. ON SHEET ${'{next}'}`, xe, BUS_Y - 2.5, 5, { anchor: 'end' }));
    }

    // Columns
    for (const c of sheetCols) {
      if (c.kind === 'label') o.push(text(`SECT. ${c.section}`, c.x + 1.5, BUS_Y + 3.8, 5, { bold: true }));
      else if (c.kind === 'out') drawOutgoing(o, c, c.x, opts);
      else if (c.kind === 'in' || c.kind === 'tie') drawIncoming(o, c, c.x);
      else if (c.kind === 'gap' && c.tie) {
        o.push(line(c.x, BUS_Y, c.x + 3, BUS_Y, 1.4));
        breakerH(o, c.x + 3, c.x + c.w - 3, BUS_Y);
        o.push(line(c.x + c.w - 3, BUS_Y, c.x + c.w, BUS_Y, 1.4));
        o.push(text(c.tie.tag, c.x + c.w / 2, BUS_Y - 5, 5, { anchor: 'middle', bold: true }));
        if (c.tie.type || c.tie.setting) o.push(text(fit([c.tie.type, c.tie.setting].filter(Boolean).join(' '), c.w + 6, 4.5), c.x + c.w / 2, BUS_Y + 6.5, 4.5, { anchor: 'middle' }));
      }
    }

    // Circuit table: label block on the left, then cells only under the outgoing breakers
    const outCols = sheetCols.filter(c => c.kind === 'out');
    if (outCols.length) {
      const yEnd = TABLE_Y + ROWS.reduce((t, r) => t + r.h, 0);
      const hLines = (xa, xb) => {
        let y = TABLE_Y;
        o.push(line(xa, y, xb, y, 0.25));
        for (const r of ROWS) { y += r.h; o.push(line(xa, y, xb, y, r.key === 'kw' ? 0.25 : 0.15)); }
      };
      hLines(AREA.x1, X0 - 4);
      o.push(line(AREA.x1, TABLE_Y, AREA.x1, yEnd, 0.25));
      o.push(line(X0 - 4, TABLE_Y, X0 - 4, yEnd, 0.25));
      let y = TABLE_Y;
      for (const r of ROWS) {
        o.push(text(r.label.toUpperCase(), AREA.x1 + 2, y + Math.min(r.h, 5) - 1.3, 5, { bold: true }));
        y += r.h;
      }
      // Runs of adjacent outgoing columns share one block
      let run = null;
      const flush = () => { if (run) { hLines(run.x1, run.x2); o.push(line(run.x1, TABLE_Y, run.x1, yEnd, 0.15)); } run = null; };
      for (const c of sheetCols) {
        if (c.kind !== 'out') { flush(); continue; }
        if (run && run.x2 === c.x) run.x2 = c.x + c.w; else { flush(); run = { x1: c.x, x2: c.x + c.w }; }
      }
      flush();
    }

    return {
      key:   board._id,
      title: `${board.name}${board.description ? ` — ${board.description}` : ''}${sheets.length > 1 ? ` (${si + 1}/${sheets.length})` : ''}`,
      items: o,
      part:  si, parts: sheets.length,
    };
  });
}

// ── Overview sheet ──
const LEVEL_H = 66;

function overviewPage(data) {
  const boards = data.boards;
  const byId = new Map(boards.map(b => [b._id, b]));
  // Feeds into a board: directly from a breaker on another board, or through a transformer / UPS.
  // The first feed decides where the board sits in the tree; any others are drawn as extra feeders.
  const feeds = [];
  for (const b of boards) {
    if (b.fedFrom && byId.has(b.fedFrom.board) && b.fedFrom.board !== b._id) {
      feeds.push({ child: b._id, parent: b.fedFrom.board, breaker: b.fedFrom.tag, incomer: b.breakers.find(x => x._id === b.incomingBreaker)?.tag });
    }
  }
  for (const s of data.sources) {
    if (!s.fedFrom || !s.switchboard || !byId.has(s.fedFrom.board) || !byId.has(s.switchboard) || s.fedFrom.board === s.switchboard) continue;
    feeds.push({ child: s.switchboard, parent: s.fedFrom.board, breaker: s.fedFrom.tag, via: s, incomer: byId.get(s.switchboard).breakers.find(x => x._id === s.incomingBreaker)?.tag });
  }
  const treeParent = new Map();
  for (const f of feeds) if (!treeParent.has(f.child)) treeParent.set(f.child, f.parent);
  // Break loops
  for (const b of boards) {
    const seen = new Set([b._id]); let cur = treeParent.get(b._id);
    while (cur) { if (seen.has(cur)) { treeParent.delete(b._id); break; } seen.add(cur); cur = treeParent.get(cur); }
  }

  const children = new Map(boards.map(b => [b._id, []]));
  for (const [child, parent] of treeParent) children.get(parent).push(child);
  const feedsFrom = (bid) => feeds.filter(f => f.parent === bid && pos.has(f.child));
  // Sources drawn above a board: everything supplying it that isn't a transformer / UPS fed from another board
  const viaIds = new Set(feeds.filter(f => f.via).map(f => f.via._id));
  const ownSources = (bid) => data.sources.filter(s => s.switchboard === bid && !viaIds.has(s._id));
  const inUse = (b) => b.breakers.length || data.sources.some(s => s.switchboard === b._id) || treeParent.has(b._id) || children.get(b._id).length;
  const pos = new Map();

  const slotW = (f) => (f.via ? 38 : 20);
  const nodeW = (bid) => Math.max(34, 20 * ownSources(bid).length, feeds.filter(f => f.parent === bid).reduce((w, f) => w + slotW(f), 0));
  const subW = new Map();
  const measure = (bid) => {
    const kids = children.get(bid);
    const kw = kids.reduce((s, k) => s + measure(k), 0) + Math.max(0, kids.length - 1) * 26;
    const w = Math.max(nodeW(bid), kw);
    subW.set(bid, w);
    return w;
  };
  const roots = boards.filter(b => !treeParent.has(b._id) && inUse(b)).map(b => b._id);
  roots.forEach(measure);

  const place = (bid, left, level) => {
    const w = subW.get(bid);
    pos.set(bid, { cx: left + w / 2, y: 42 + level * LEVEL_H, level });
    const kids = children.get(bid);
    const kw = kids.reduce((s, k) => s + subW.get(k), 0) + Math.max(0, kids.length - 1) * 26;
    let x = left + (w - kw) / 2;
    for (const k of kids) { place(k, x, level + 1); x += subW.get(k) + 26; }
  };
  let left = 30;
  for (const r of roots) { place(r, left, 0); left += subW.get(r) + 30; }

  const o = [];
  for (const [bid, p] of pos) {
    const b = byId.get(bid);
    const w = nodeW(bid);
    const x1 = p.cx - w / 2, x2 = p.cx + w / 2;
    o.push(line(x1, p.y, x2, p.y, 1.4));
    o.push(text(b.name, x1 - 2, p.y - 0.5, 8, { anchor: 'end', bold: true }));
    if (b.voltage) o.push(text(b.voltage, x1 - 2, p.y + 3, 5.5, { anchor: 'end' }));
    if (b.sections.length > 1) o.push(text(`SECT. ${b.sections.join('/')}`, x1 - 2, p.y + 5.8, 4.5, { anchor: 'end' }));

    // Own sources above the busbar
    const srcs = ownSources(bid);
    srcs.forEach((s, i) => {
      const sx = x1 + (w * (i + 0.5)) / srcs.length;
      const inc = b.breakers.find(x => x._id === s.incomingBreaker);
      o.push(line(sx, p.y, sx, p.y - 6));
      if (inc) { breakerV(o, sx, p.y - 15, p.y - 6); o.push(text(inc.tag, sx + 2.2, p.y - 9, 4.5)); }
      o.push(line(sx, inc ? p.y - 15 : p.y - 6, sx, p.y - 19));
      const kindH = s.kind === 'transformer' ? 7 : s.kind === 'generator' ? 5.5 : 4;
      sourceSymbol(o, s.kind, sx, p.y - 19 - kindH);
      const top = p.y - 19 - 2 * kindH;
      o.push(text(fit(s.name, 17, 4.5), sx, top - 4.2, 4.5, { anchor: 'middle', bold: true }));
      o.push(text(fit(ratingText(s).split(' / ').slice(0, 2).join(' / '), 17, 4), sx, top - 1.6, 4, { anchor: 'middle' }));
    });

    // Feeders down to the boards this one supplies
    // Several feeds into one board land side by side
    const landX = (l) => {
      const c = pos.get(l.child);
      const into = feeds.filter(f => f.child === l.child && pos.has(f.parent));
      const cw = nodeW(l.child);
      return into.length > 1 ? c.cx - cw / 2 + (cw * (into.indexOf(l) + 0.5)) / into.length : c.cx;
    };
    // Feeder breakers in the same order as where they land, so the lines never cross
    const out = feedsFrom(bid).map(l => ({ l, lx: landX(l) })).sort((p1, p2) => p1.lx - p2.lx);
    const used = out.reduce((t, f) => t + slotW(f.l), 0);
    let slotX = x1 + (w - used) / 2;
    for (const f of out) { f.kx = slotX + 6; slotX += slotW(f.l); }
    // Each horizontal run gets its own height: the further a feed reaches sideways, the higher it runs
    const lane = new Map();
    const lefts = out.filter(f => f.lx < f.kx - 0.5), rights = out.filter(f => f.lx > f.kx + 0.5).reverse();
    for (const side of [lefts, rights]) side.forEach((f, i) => lane.set(f, side.length - 1 - i));
    const lanes = Math.max(lefts.length, rights.length);
    const laneGap = Math.min(3, 16 / Math.max(1, lanes));
    out.forEach((f) => {
      const { l, lx, kx } = f;
      const c = pos.get(l.child);
      const into = feeds.filter(g => g.child === l.child && pos.has(g.parent));
      o.push(line(kx, p.y, kx, p.y + 5));
      breakerV(o, kx, p.y + 5, p.y + 14);
      o.push(text(l.breaker, kx + 2.2, p.y + 9, 4.5));
      let yy = p.y + 14;
      if (l.via) {
        o.push(line(kx, yy, kx, yy + 4));
        sourceSymbol(o, l.via.kind, kx, yy + 4 + (l.via.kind === 'transformer' ? 7 : 4));
        const h = l.via.kind === 'transformer' ? 14 : 8;
        o.push(text(fit(l.via.name, 26, 4.5), kx + 6.5, yy + 4 + h / 2 - 0.5, 4.5, { bold: true }));
        o.push(text(fit(ratingText(l.via).split(' / ').slice(0, 2).join(' / '), 26, 4), kx + 6.5, yy + 4 + h / 2 + 2, 4));
        yy += 4 + h;
      }
      const mid = c.y - 16 - (lane.get(f) || 0) * laneGap - into.indexOf(l) * 1.5;
      o.push(line(kx, yy, kx, mid));
      o.push(line(kx, mid, lx, mid));
      if (l.incomer) {
        o.push(line(lx, mid, lx, c.y - 12));
        breakerV(o, lx, c.y - 12, c.y - 3);
        o.push(text(l.incomer, lx + 2.2, c.y - 6.5, 4.5));
        o.push(line(lx, c.y - 3, lx, c.y));
      } else o.push(line(lx, mid, lx, c.y));
    });
  }

  // Bus ties between different boards: dashed link with the tie breaker's tag
  for (const b of boards) for (const t of b.breakers.filter(x => x.tieBoard && x.tieBoard !== b._id)) {
    const a = pos.get(b._id), z = pos.get(t.tieBoard);
    if (!a || !z) continue;
    const ax = a.cx + nodeW(b._id) / 2, zx = z.cx - nodeW(t.tieBoard) / 2;
    o.push(line(ax, a.y, zx, z.y, 0.3, { dash: true }));
    o.push(text(`${t.tag} BUS TIE`, (ax + zx) / 2, (a.y + z.y) / 2 - 1.5, 4.5, { anchor: 'middle' }));
  }

  // Fit to the drawing area
  const xs = [], ys = [];
  for (const it of o) {
    if (it.t === 'line') { xs.push(it.x1, it.x2); ys.push(it.y1, it.y2); }
    if (it.t === 'circle') { xs.push(it.cx - it.r, it.cx + it.r); ys.push(it.cy - it.r, it.cy + it.r); }
    if (it.t === 'text') { xs.push(it.anchor === 'end' ? it.x - textWidth(it.s, it.size) : it.x, it.anchor === 'end' ? it.x : it.x + textWidth(it.s, it.size)); ys.push(it.y - capH(it.size), it.y); }
  }
  if (!xs.length) return { key: 'overview', title: 'Overview', items: [text('No switchboards yet.', AREA.x1 + 4, AREA.y1 + 10, 10)] };
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const areaW = AREA.x2 - AREA.x1, areaH = TB.y - 6 - AREA.y1;
  const s = Math.min(1.4, areaW / (maxX - minX), areaH / (maxY - minY));
  const dx = AREA.x1 + (areaW - (maxX - minX) * s) / 2 - minX * s;
  const dy = AREA.y1 + 2 - minY * s;
  return { key: 'overview', title: 'Overview', items: [], group: { s, dx, dy, items: o } };
}

// ── All sheets ──
// opts: { cables, kw } — print the cable / kW rows of the circuit tables
export function buildSld(data, info, opts = {}) {
  const pages = [overviewPage(data)];
  for (const b of data.boards) {
    const hasContent = b.breakers.length || data.sources.some(s => s.switchboard === b._id) || b.fedFrom;
    if (hasContent) pages.push(...boardPages(b, data, opts));
  }
  const total = pages.length;
  pages.forEach((p, i) => {
    p.no = i + 1; p.total = total;
    // Continuation references between the sheets of one board
    p.items = p.items.map(it => (it.t === 'text' && it.s.includes('{') ? { ...it, s: it.s.replace('{prev}', String(i)).replace('{next}', String(i + 2)) } : it));
    p.frame = frame(p, info);
  });
  return pages;
}
